import axios from 'axios';
import { useCallback, useState } from 'react';

/**
 * Two-step presigned-PUT upload chain used by the self-help exercise
 * "proof" upload flow and any future direct-to-S3 upload surface:
 *
 *   1. `declareUpload(file)` — caller hits its own API to obtain
 *      `{ uploadUrl, key }` (e.g. POST /engagement/self-help/exercises/:id/upload-proof).
 *   2. The hook PUTs the raw file body to `uploadUrl` with
 *      `Content-Type: <file.type>` (no multipart, no FormData — must
 *      match byte-for-byte what the presign was signed against).
 *
 * The hook returns the stable `key` on success so the caller can store
 * it in its own form state and send it back on the next /progress or
 * /submit call.
 *
 * No automatic retries. Callers `reset()` then call `upload(file)`
 * again when the user clicks "retry".
 */
export type S3UploadStatus = 'idle' | 'declaring' | 'uploading' | 'success' | 'error';

export interface DeclareUploadResult {
  /** Presigned S3 PUT URL. The Content-Type on the PUT MUST match what
   *  the caller sent during the declare step. */
  uploadUrl: string;
  /** The S3 object key (or whatever stable string the caller wants
   *  echoed back on success — usually the key the exercise structure
   *  field will store). */
  key: string;
  /** Optional TTL hint from the backend; the hook does not act on this. */
  expiresIn?: number;
}

export interface UseS3UploadOptions {
  declareUpload: (file: File) => Promise<DeclareUploadResult>;
  /** Reject files larger than this up front. Default 10 MB. */
  maxSizeBytes?: number;
  /**
   * Allowlist of MIME types. If set and the file's type is not in the
   * list, `upload()` rejects before declaring. Leave undefined to
   * accept any content type.
   */
  acceptedContentTypes?: readonly string[];
}

export interface UseS3UploadResult {
  /** Run the two-step chain. Resolves to the S3 key on success or `null`
   *  on failure (inspect `error` for details). */
  upload: (file: File) => Promise<string | null>;
  /** Clear status / progress / error / key back to `idle`. Does NOT
   *  mutate the consumer's form value — caller owns that. */
  reset: () => void;
  status: S3UploadStatus;
  /** 0..100; jumps to 100 on success. */
  progress: number;
  error: Error | null;
  /** Populated on success with the `key` from `declareUpload`. */
  key: string | null;
  /** Last file's name, kept through success/error states so the UI can
   *  render "document.pdf — upload failed" / "document.pdf — uploaded". */
  filename: string | null;
}

const DEFAULT_MAX_BYTES = 10 * 1024 * 1024;

export function useS3Upload({
  declareUpload,
  maxSizeBytes = DEFAULT_MAX_BYTES,
  acceptedContentTypes,
}: UseS3UploadOptions): UseS3UploadResult {
  const [status, setStatus] = useState<S3UploadStatus>('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<Error | null>(null);
  const [key, setKey] = useState<string | null>(null);
  const [filename, setFilename] = useState<string | null>(null);

  const validate = useCallback(
    (file: File): Error | null => {
      if (file.size > maxSizeBytes) {
        const mb = Math.max(1, Math.round(maxSizeBytes / (1024 * 1024)));
        return new Error(`File is too large. Max ${String(mb)} MB.`);
      }
      if (acceptedContentTypes && acceptedContentTypes.length > 0) {
        const ct = file.type || 'application/octet-stream';
        if (!acceptedContentTypes.includes(ct)) {
          return new Error(`File type "${ct}" is not supported.`);
        }
      }
      return null;
    },
    [maxSizeBytes, acceptedContentTypes],
  );

  const upload = useCallback(
    async (file: File): Promise<string | null> => {
      const validationError = validate(file);
      if (validationError) {
        setFilename(file.name);
        setError(validationError);
        setStatus('error');
        return null;
      }

      setError(null);
      setKey(null);
      setProgress(0);
      setFilename(file.name);
      setStatus('declaring');

      let declared: DeclareUploadResult;
      try {
        declared = await declareUpload(file);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        setStatus('error');
        return null;
      }

      setStatus('uploading');
      try {
        await axios.put(declared.uploadUrl, file, {
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          onUploadProgress: (e) => {
            if (e.total && e.total > 0) {
              const pct = Math.min(100, Math.round((e.loaded / e.total) * 100));
              setProgress(pct);
            }
          },
        });
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        setStatus('error');
        return null;
      }

      setKey(declared.key);
      setProgress(100);
      setStatus('success');
      return declared.key;
    },
    [declareUpload, validate],
  );

  const reset = useCallback(() => {
    setStatus('idle');
    setProgress(0);
    setError(null);
    setKey(null);
    setFilename(null);
  }, []);

  return { upload, reset, status, progress, error, key, filename };
}
